import minBy from "lodash/minBy";
import maxBy from "lodash/maxBy";
import groupBy from "lodash/groupBy";
import uniq from "lodash/uniq";
import ahoy from "ahoy.js";
import { Badge } from "@/components/ui/badge";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemHeader,
  ItemTitle,
} from "@/components/ui/item";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import useFilteredProducts from "@/hooks/use-filtered-products";
import { getProductState } from "@/lib/utils";
import { Collection, StorefrontStorePageProps } from "@/types";
import { Link, router, usePage } from "@inertiajs/react";
import uniqBy from "lodash/uniqBy";
import sortBy from "lodash/sortBy";
import { Search } from "lucide-react";
import { ReactNode, useLayoutEffect, useMemo } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import ImageWithFallback from "@/components/application/image-with-fallback";
import {
  storefront_collection_path,
  storefront_product_path,
} from "@/lib/routes";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import StorefrontLayout from "@/components/application/storefront-layout";
import { COLORS_BY_ID } from "@/lib/colors";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import useTranslations from "@/hooks/use-translations";

function CollectionsSection({ collections }: { collections: Collection[] }) {
  const isMobile = useIsMobile();

  if (collections.length === 0) {
    return null;
  }

  if (isMobile) {
    return (
      <div className="flex flex-col gap-4">
        {collections.map((collection) => (
          <Link
            key={collection.id}
            href={storefront_collection_path(collection)}
          >
            <Card className="relative overflow-hidden w-full aspect-auto justify-center">
              {collection.cover_image_url && (
                <>
                  <div className="absolute inset-0 z-20 aspect-video bg-black/45" />
                  <img
                    src={collection.cover_image_url}
                    className="absolute inset-0 aspect-video w-full object-cover brightness-60 dark:brightness-40"
                  />
                </>
              )}
              <CardContent className="z-30 text-white relative">
                <CardTitle>{collection.name}</CardTitle>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-4 flex-wrap">
      {collections.map((collection) => (
        <Link key={collection.id} href={storefront_collection_path(collection)}>
          <Card className="relative overflow-hidden h-48 aspect-video justify-center">
            {collection.cover_image_url && (
              <>
                <div className="absolute inset-0 z-20 aspect-video bg-black/45" />
                <img
                  src={collection.cover_image_url}
                  className="absolute inset-0 aspect-video w-full object-cover brightness-60 dark:brightness-40 group-hover:grayscale-0"
                />
              </>
            )}
            <CardContent className="z-30 text-white relative">
              <CardTitle>{collection.name}</CardTitle>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

function ResponsiveFilter({
  items,
  value,
  onChange,
}: {
  items: { value: string; label: string; ariaLabel: string }[];
  value: string;
  onChange: (newValue: string) => void;
}) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Select
        defaultValue={items[0].value}
        value={value}
        onValueChange={onChange}
      >
        <SelectTrigger className="text-lg md:text-sm w-full md:w-96">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <ToggleGroup
      type="single"
      variant="outline"
      value={value}
      onValueChange={onChange}
    >
      {items.map((item) => (
        <ToggleGroupItem
          key={item.value}
          value={item.value}
          aria-label={item.ariaLabel}
          className="text-lg md:text-sm"
        >
          {item.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

function PickABrick() {
  const { current_store, parts: products } =
    usePage<StorefrontStorePageProps>().props;

  if (!products) {
    return null;
  }

  const productsByPartNum = groupBy(products, "part_num");
  const uniqPartNums = uniq(products.map((product) => product.part_num));

  return (
    <ItemGroup className="grid grid-cols-1 md:grid-cols-[repeat(4,minmax(280px,1fr))] gap-6">
      {uniqPartNums.map((partNum) => {
        if (!partNum) {
          return null;
        }

        const product = productsByPartNum[partNum][0];

        if (!product) {
          return null;
        }

        const productsByColor = groupBy(productsByPartNum[partNum], "color_id");

        const minPrice = minBy(productsByPartNum[partNum], "price_cents");
        const maxPrice = maxBy(productsByPartNum[partNum], "price_cents");

        const minPriceMaxPriceSame =
          minPrice?.price_cents === maxPrice?.price_cents;

        const colors = productsByPartNum[partNum]
          .filter((product) => product.color_id)
          .map((product) => COLORS_BY_ID[product.color_id!]);

        return (
          <Item key={partNum} variant="outline">
            <ItemHeader className="relative aspect-square">
              <Badge
                variant="secondary"
                className="absolute left-0 top-1 z-1 text-sm md:text-xs"
              >
                {partNum}
              </Badge>
              <ImageWithFallback
                srcset={[product.custom_image_url, product.image_url]}
                alt={product.name}
                className="relative w-full rounded-sm m-auto object-contain aspect-square p-20"
              />
            </ItemHeader>
            <ItemContent>
              <ItemTitle className="text-base md:text-sm">
                {product.name}
              </ItemTitle>
              {colors.length === 1 ? (
                <nav className="flex flex-wrap gap-2">
                  {colors.map((color) => (
                    <div
                      key={color.id}
                      className="size-4 rounded-xs border border-border"
                      style={{ background: `#${color.rgb}` }}
                      title={color.name}
                    ></div>
                  ))}
                </nav>
              ) : (
                <Accordion type="multiple">
                  <AccordionItem value="colors">
                    <AccordionTrigger>
                      <nav className="flex flex-wrap gap-2">
                        {colors.map((color) => (
                          <div
                            key={color.id}
                            className="size-4 rounded-xs border border-border"
                            style={{ background: `#${color.rgb}` }}
                            title={color.name}
                          ></div>
                        ))}
                      </nav>
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="flex-col gap-2">
                        {colors.map((color) => (
                          <li key={color.id} className="flex justify-between">
                            <span>{color.name}</span>
                            <span className="font-semibold text-muted-foreground">
                              {new Intl.NumberFormat(navigator.language, {
                                style: "currency",
                                currency:
                                  current_store.currency.toUpperCase() || "USD",
                              }).format(
                                productsByColor[color.id][0].price_cents / 100
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
              <ItemDescription>
                {minPriceMaxPriceSame && minPrice && (
                  <span className="font-semibold text-lg md:text-base">
                    {new Intl.NumberFormat(navigator.language, {
                      style: "currency",
                      currency: current_store.currency.toUpperCase() || "USD",
                    }).format(minPrice.price_cents / 100)}
                  </span>
                )}
                {!minPriceMaxPriceSame && minPrice && maxPrice && (
                  <span className="font-semibold text-lg md:text-base">
                    {new Intl.NumberFormat(navigator.language, {
                      style: "currency",
                      currency: current_store.currency.toUpperCase() || "USD",
                    }).format(minPrice.price_cents / 100)}{" "}
                    -{" "}
                    {new Intl.NumberFormat(navigator.language, {
                      style: "currency",
                      currency: current_store.currency.toUpperCase() || "USD",
                    }).format(maxPrice.price_cents / 100)}
                  </span>
                )}
              </ItemDescription>
            </ItemContent>
          </Item>
        );
      })}
    </ItemGroup>
  );
}

function StorePage() {
  const isMobile = useIsMobile();
  const { t } = useTranslations("storefront.stores.show");

  const { current_store, products, collections } =
    usePage<StorefrontStorePageProps>().props;

  const allThemes = useMemo(() => {
    let themes = sortBy(
      uniqBy(
        products.map((product) => product.theme).filter((theme) => !!theme),
        "id"
      ),
      "name"
    );

    // Normalize themes with missing parents
    themes = themes.map((theme) => {
      if (theme.parent_id && !themes.find((t) => t.id === theme.parent_id)) {
        return { ...theme, parent_id: null };
      }

      return theme;
    });

    const themesByParent = groupBy(themes, "parent_id");

    return (themesByParent["null"] ?? []).map((theme) => ({
      ...theme,
      subthemes: [theme, ...(themesByParent[theme.id] || [])],
    }));
  }, [products]);

  const { filter, setFilter, search, setSearch, filteredProducts } =
    useFilteredProducts(products, { updateUrl: true });

  function onProductTypeChange(value: string) {
    value ||= "all";
    ahoy.track("Filtered store products", {
      store_id: current_store.id,
      product_type: value,
    });
    setFilter({ product_type: value });
  }

  function onStateChange(value: string) {
    value ||= "all";
    ahoy.track("Filtered store products", {
      store_id: current_store.id,
      state: value,
    });

    setFilter({ state: value });
  }

  function onThemeChange(value: string) {
    value ||= "all";
    ahoy.track("Filtered store products", {
      store_id: current_store.id,
      theme: value,
    });
    setFilter({ theme: value });
  }

  useLayoutEffect(() => {
    ahoy.track("Viewed store", { store_id: current_store.id });
  }, [current_store.id]);

  return (
    <div className="flex w-full flex-col gap-6 p-4 overflow-auto">
      <CollectionsSection collections={collections} />

      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <ToggleGroup
          type="single"
          variant="outline"
          value={filter.product_type}
          onValueChange={onProductTypeChange}
          size={isMobile ? "lg" : undefined}
        >
          <ToggleGroupItem
            value="all"
            aria-label={t("filters.show_all")}
            className="text-lg md:text-sm"
          >
            {t("filters.all")}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="set"
            aria-label={t("filters.show_sets")}
            className="text-lg md:text-sm"
          >
            {t("filters.sets")}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="minifigure"
            aria-label={t("filters.show_minifigures")}
            className="text-lg md:text-sm"
          >
            {t("filters.minifigures")}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="part"
            aria-label={t("filters.show_parts")}
            className="text-lg md:text-sm"
          >
            {t("filters.parts")}
          </ToggleGroupItem>
        </ToggleGroup>

        {filter.product_type !== "part" && (
          <>
            <ResponsiveFilter
              items={[
                {
                  value: "all",
                  label: t("filters.all"),
                  ariaLabel: t("filters.show_all"),
                },
                {
                  value: "new_complete",
                  label: t("filters.new"),
                  ariaLabel: t("filters.show_new"),
                },
                {
                  value: "new_sealed",
                  label: t("filters.new_sealed"),
                  ariaLabel: t("filters.show_new_sealed"),
                },
                {
                  value: "pre_owned",
                  label: t("filters.pre_owned"),
                  ariaLabel: t("filters.show_pre_owned"),
                },
                {
                  value: "pre_owned_with_box",
                  label: t("filters.pre_owned_with_box"),
                  ariaLabel: t("filters.show_pre_owned_with_box"),
                },
              ]}
              value={filter.state}
              onChange={onStateChange}
            />

            <Select value={filter.theme} onValueChange={onThemeChange}>
              <SelectTrigger className="text-lg md:text-sm w-full md:w-96">
                <SelectValue placeholder={t("theme_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-lg md:text-sm">
                  {t("all_themes")}
                </SelectItem>
                {allThemes.map((theme, index) =>
                  theme.subthemes.length > 1 ? (
                    <SelectGroup key={theme.id} className="text-lg md:text-sm">
                      <SelectLabel className="text-sm md:text-xs">
                        {theme.name}
                      </SelectLabel>
                      {theme.subthemes.map((subtheme) => (
                        <SelectItem
                          key={subtheme.id}
                          value={subtheme.id}
                          className="text-lg md:text-sm pl-4"
                        >
                          {subtheme.name}
                        </SelectItem>
                      ))}
                      {index < allThemes.length - 1 && <SelectSeparator />}
                    </SelectGroup>
                  ) : (
                    <SelectItem
                      key={theme.id}
                      value={theme.id}
                      className="text-lg md:text-sm"
                    >
                      {theme.name}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>

            <InputGroup>
              <InputGroupInput
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("search_placeholder")}
                className="text-lg md:text-sm"
              />
              <InputGroupAddon>
                <Search />
              </InputGroupAddon>
            </InputGroup>
          </>
        )}
      </div>

      {filter.product_type !== "part" && (
        <ItemGroup className="grid grid-cols-1 md:grid-cols-[repeat(4,minmax(280px,1fr))] gap-6">
          {filteredProducts.map((product) => (
            <Item key={product.id} variant="outline" asChild>
              <Link href={storefront_product_path(product.id)}>
                <ItemHeader className="relative aspect-square">
                  <Badge
                    variant="secondary"
                    className="absolute left-0 top-1 z-1 text-sm md:text-xs"
                  >
                    {product.system_code}
                  </Badge>
                  <Badge
                    variant="outline"
                    className="absolute right-0 top-1 z-1 text-sm md:text-xs"
                  >
                    {getProductState(product, t)}
                  </Badge>
                  <ImageWithFallback
                    srcset={[product.custom_image_url, product.image_url]}
                    alt={product.name}
                    className="relative w-full rounded-sm m-auto object-contain aspect-square"
                  />
                </ItemHeader>
                <ItemContent>
                  <ItemTitle className="text-base md:text-sm">
                    {product.name}
                  </ItemTitle>
                  <ItemDescription>
                    <span className="font-semibold text-lg md:text-base">
                      {new Intl.NumberFormat(navigator.language, {
                        style: "currency",
                        currency: current_store.currency.toUpperCase() || "USD",
                      }).format(product.price_cents / 100)}
                    </span>
                  </ItemDescription>
                </ItemContent>
              </Link>
            </Item>
          ))}
        </ItemGroup>
      )}
      {filter.product_type === "part" && <PickABrick />}
    </div>
  );
}

StorePage.layout = (page: ReactNode) => <StorefrontLayout children={page} />;

export default StorePage;
